/**
 * useSessionRestore
 * -----------------
 * Restores the session on a cold start (page refresh, or the PWA being closed
 * and reopened).
 *
 * The access token is deliberately NOT kept in localStorage — that is an XSS
 * decision worth keeping. What survives a restart is the httpOnly refresh
 * cookie, which the browser holds and no script can read. So on boot we ask
 * the server once whether that cookie is still good:
 *
 *   - cookie valid   -> a fresh access token, the user never sees a login screen
 *   - cookie expired -> `ready` with no session, and the guards send them to
 *                       sign in as before
 *
 * Sessions therefore last exactly as long as the backend's per-app
 * `REFRESH_TOKEN_EXPIRE_MINUTES_*` says they should, rather than dying the
 * moment the tab closes.
 *
 * `ready` is what stops the flicker: until the attempt settles the router must
 * not decide the visitor is signed out, or every reload bounces through
 * /login and back.
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authApi, userApi } from '@/services/api';
import { loginSuccess, logout } from '@/store/authSlice';

export function useSessionRestore() {
  const dispatch = useDispatch();
  const accessToken = useSelector((s) => s.auth.accessToken);
  const user = useSelector((s) => s.auth.user);

  // A token restored from storage carries no profile, and ProtectedRoute reads
  // "signed in, no profile" as a half-finished signup — so the boot is not done
  // until the profile is back, or we know there is no session.
  const [ready, setReady] = useState(Boolean(accessToken && user));

  useEffect(() => {
    let cancelled = false;
    if (accessToken && user) {
      setReady(true);
      return undefined;
    }

    (async () => {
      // Never let a slow or unreachable API hold the whole app hostage — but
      // releasing the router while the refresh is still in flight is its own
      // bug: the guard sees an empty store, navigates to /login, and then the
      // refresh lands and bounces back to the dashboard. That visible
      // login-screen flash is exactly what `ready` exists to prevent.
      //
      // So the deadline is long enough that it is only ever reached when the
      // API is genuinely unreachable (the refresh call itself is bounded by
      // the axios timeout well inside this), and when it IS reached the
      // in-flight attempt is abandoned rather than allowed to land late.
      const guard = setTimeout(() => {
        if (!cancelled) {
          cancelled = true;
          setReady(true);
        }
      }, 12000);
      try {
        // A token was restored from storage: use it, and just re-fetch the
        // profile. Refreshing first would sign the user out whenever the
        // cookie is missing, which is the whole bug.
        if (accessToken) {
          // Up to two attempts: a single network blip must not decide whether
          // this person has an account.
          for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
              const profile = await userApi.getProfile();
              if (!cancelled) {
                dispatch(loginSuccess({
                  accessToken, user: profile || null, profileLoaded: true,
                }));
              }
              return;
            } catch (err) {
              const status = err?.response?.status;
              if (status === 401) {
                // The stored token really is dead. Clearing it here is what
                // stops the app from firing a storm of 401s with a token it
                // never validated — which is exactly what a long free scan hit.
                dispatch(logout());
                break;
              }
              if (status === 404) {
                // The server answered: no profile. A genuine pre-signup guest.
                if (!cancelled) {
                  dispatch(loginSuccess({ accessToken, user: null, profileLoaded: true }));
                }
                return;
              }
              if (attempt === 0) {
                await new Promise((r) => { setTimeout(r, 700); });
                continue;
              }
              // Still unreachable. Leave profileLoaded false so nothing
              // downstream mistakes "could not ask" for "has no account".
              return;
            }
          }
        }

        const data = await authApi.refreshToken();
        if (cancelled) return;
        if (!data?.accessToken) {
          if (!accessToken) dispatch(logout());
          return;
        }

        // /auth/refresh returns a token only. The profile has to come with it:
        // the route guard treats "signed in, no profile" as a half-finished
        // signup and would send a perfectly normal client to the signup wizard
        // on every reload.
        dispatch(loginSuccess({
          accessToken: data.accessToken, user: null, profileLoaded: false,
        }));
        try {
          const profile = await userApi.getProfile();
          if (!cancelled) {
            dispatch(loginSuccess({
              accessToken: data.accessToken, user: profile || null, profileLoaded: true,
            }));
          }
        } catch (err) {
          // Only a 404 means "this phone has no profile". Any other failure
          // means we could not ask — and answering "no account" on a network
          // error is what put the signup wizard in front of existing clients.
          if (err?.response?.status === 404 && !cancelled) {
            dispatch(loginSuccess({
              accessToken: data.accessToken, user: null, profileLoaded: true,
            }));
          }
        }
      } catch {
        // Refresh failed. That is NOT proof the session is over: in this
        // deployment the cookie frequently never arrives, and discarding a
        // perfectly good stored token here is exactly what signed people out
        // on every relaunch. Keep it and let the 401 interceptor decide — a
        // token the server rejects will fail on the first real request.
        if (!cancelled && !accessToken) dispatch(logout());
      } finally {
        clearTimeout(guard);
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
    // Runs once on mount: this is a cold-start question, not a reactive one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ready;
}

export default useSessionRestore;
